/**
 * TODO:
 * - Use `size` prop as `textProps`
 */
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import propTypes from '@styled-system/prop-types';
import PropTypes from 'prop-types';
import {
  color,
  compose,
  flexbox,
  layout,
  position,
  space,
  typography,
} from 'styled-system';

import { forwardValidProp } from '../../utils/style';
import Text from '../Text';

const baseTooltipStyles = ({ theme }) => css`
  background: ${theme.colors.tooltip.background};
  border-radius: ${theme.border.radius.size2};
  cursor: default;
  padding: ${theme.space[2]};
  position: absolute;
  transition: ${theme.transitions.fast};
  user-select: none;
  z-index: 2;
`;

const directionTooltipStyles = ({ direction, theme }) =>
  (direction === 'bottom' &&
    css`
      top: ${`calc(100% + ${theme.space[1]})`};
    `) ||
  (direction === 'top' &&
    css`
      bottom: ${`calc(100% + ${theme.space[1]})`};
    `) ||
  (direction === 'right' &&
    css`
      left: ${`calc(100% + ${theme.space[1]})`};
    `) ||
  (direction === 'left' &&
    css`
      right: ${`calc(100% + ${theme.space[1]})`};
    `);

const displayTooltipStyles = ({ isOpen }) =>
  isOpen
    ? css`
        visibility: visible;
        opacity: 1;
      `
    : css`
        visibility: hidden;
        opacity: 0;
      `;

const autoAlignTooltipStyles = ({ autoAlign, direction }) =>
  ((direction === 'bottom' || direction === 'top') &&
    autoAlign &&
    css`
      left: ${autoAlign && '50%'};
      transform: ${autoAlign && 'translate(-50%, 0)'};
    `) ||
  ((direction === 'right' || direction === 'left') &&
    autoAlign &&
    css`
      top: ${autoAlign && '50%'};
      transform: ${autoAlign && 'translate(0, -50%)'};
    `);

const StyledTooltip = styled('div', {
  shouldForwardProp: (prop) => forwardValidProp(prop),
})`
  ${baseTooltipStyles}
  ${autoAlignTooltipStyles}
  ${directionTooltipStyles}
  ${displayTooltipStyles}
  ${compose(color, flexbox, layout, position, typography, space)};
`;

const StyledText = styled(Text)`
  ${({ theme }) => css`
    color: ${theme.colors.tooltip.foreground};
  `}
`;

function Tooltip({ children, size, textProps, ...rest }) {
  return (
    <StyledTooltip role="tooltip" aria-hidden="true" {...rest}>
      <StyledText size={size} {...textProps}>
        {children}
      </StyledText>
    </StyledTooltip>
  );
}

Tooltip.defaultProps = {
  autoAlign: true,
  direction: 'bottom',
  isOpen: false,
  size: 'size3',
};

Tooltip.propTypes = {
  /**
   * Auto aligns tooltip on center using the first positioned parent component,
   * can be disabled for custom align using `position` styled-system props
   */
  autoAlign: PropTypes.bool,
  /**
   * Children to be rendered inside component
   */
  children: PropTypes.node.isRequired,
  /**
   * Relative direction for positioning purposes
   */
  direction: PropTypes.oneOf(['bottom', 'right', 'left', 'top']),
  /**
   * Required identifier to use alongside `aria-describedby` on
   * the tooltip trigger
   */
  id: PropTypes.string.isRequired,
  /**
   * Whether the dropdown menu is open
   */
  isOpen: PropTypes.bool,
  /**
   * Size of the tooltip
   */
  size: PropTypes.string,
  /**
   * Implements `styled-system` [position](https://styled-system.com/api/#position) utility
   */
  ...propTypes.position,
};

export default Tooltip;
